// ============================================================
// Blitz CRM — Pure Utility Functions (v30.06)
// Extracted from App.jsx — no React dependencies
// ============================================================

// 2026-04-18: ALL_PAGES — shared/pages.json = single source of truth для
// frontend + backend. Vite нативно поддерживает JSON import.
import sharedPages from '../shared/pages.json';
import * as tz from './lib/tz.js';   // DST-aware business time (Europe/Kyiv)
import { USER_COLORS_REF } from './lib/user-colors.js';   // per-user custom colors (user_colors фича, 2026-07-13)
import { stampNewRecord, stampUpdate } from './lib/record-stamp.js';

// ── Admin emails & check ──
export const ADMIN_EMAILS = ["y0505300530@gmail.com", "office1092021@gmail.com", "alehandro@blitz-affiliates.marketing"];
export const isAdmin = (email) => ADMIN_EMAILS.includes(email);

// ── Business-time helpers (Europe/Kyiv, DST-aware) ──
// Delegate to src/lib/tz.js (single source of truth, twin of backend/utils/tz.cjs).
// Names kept for backward-compat with all importers. Was a FIXED GMT+2 offset; now
// Europe/Kyiv DST (GMT+2 winter / GMT+3 summer, auto-switching). Winter output is
// byte-identical to before; summer is +3 — the correct Kyiv wall clock.
export const nowGMT2 = () => tz.zonedDate();            // Date whose UTC fields == Kyiv wall clock
export const todayGMT2 = () => tz.dateKey();            // 'YYYY-MM-DD' Kyiv calendar day
export const todayDisplayGMT2 = () => tz.displayDate(); // 'DD/MM/YYYY' Kyiv
// Real on-chain payment time from a txTimestamp (epoch ms) → "HH:MM" Kyiv time.
// Returns "" when there is no valid timestamp — callers then show date-only rather
// than a FABRICATED time (keep the empty-guard here; tz.fmtTime defaults to now).
export const fmtTxTimeGMT2 = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts);
  if (!n || isNaN(n)) return "";
  return tz.fmtTime(n);
};

// ── ID generation & record stamping ──
export const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().replace(/-/g,'').slice(0,9) : Math.random().toString(36).substr(2, 9));
// Thin re-exports of src/lib/record-stamp.js. Contract change vs the old
// Date.now() wrappers: stampNew now also sets createdAt; stampUpdatedAt uses
// stampUpdate (max(now, prev+1) + sticky-future guard) on serverNow().
export const stampNew = (record) => ({ ...record, ...stampNewRecord() });
export function stampUpdatedAt(record) {
  if (record && typeof record === 'object') record.updatedAt = stampUpdate(record.updatedAt);
  return record;
}

// ── Country flag emoji ──
// ISO 3166-1 alpha-2 mapping. Некоторые business-коды не совпадают с ISO
// (напр. UK = United Kingdom в нашей CRM, но Unicode flag требует GB).
// Без alias рендерится 🇺🇰 = regional indicator U+K, что валидно как sequence,
// но флага не существует → iOS/Chrome показывают placeholder "U K" в квадратиках.
const FLAG_ALIASES = { UK: 'GB', EL: 'GR' };
// v31.59: strip language suffix from compound codes — `CAen`/`CAfr`/`BEnl`/`BEfr`/`CHfr`
// etc. Без strip countryFlag('CAen').length=4, returns "" (no flag), и UI показывает
// CAen/CAfr без флага.
//
// 2026-05-22 PR (this): extended suffix к ISO 639-1 codes (it/es/pt/ar/ja/ko/zh)
// + informal jp/kr per PR#125 H2 spec. MUST stay в sync с 3 sibling regexes:
//   • src/pages/Deals/index.jsx::countryToContinent
//   • src/pages/Deals/index.jsx::baseCountryCode
//   • backend/services/deals-dedup.cjs::baseCountryCode
// Future refactor: extract к shared module (would need ESM/CJS dual export).
function stripLangSuffix(upper) {
  const m = upper.match(/^([A-Z]{2})(EN|ENG|FR|NL|DE|RU|EU|IT|ES|PT|AR|JA|JP|KO|KR|ZH)$/);
  return m ? m[1] : upper;
}
export const countryFlag = code => {
  if (!code) return "";
  const normalized = stripLangSuffix(String(code).toUpperCase());
  const iso = FLAG_ALIASES[normalized] || normalized;
  if (iso.length === 2) {
    const offset = 127397;
    return String.fromCodePoint(...[...iso].map(c => c.charCodeAt(0) + offset));
  }
  return "";
};
// ── Affiliate label → country flag(s) ──
// CRG-deals affiliate labels look like "82 DE", "51 LT LV EE", "259 UK (CPA)",
// "1000 GCC". Pull every 2-letter country code out and render its flag: the leading
// affiliate number is digits (skipped), and countryFlag() returns "" for any
// non-2-letter token, so "GCC" (a region — no flag exists) and "CPA" (a suffix) drop
// out while the real country still resolves — even mid-string before a "(CPA)" suffix
// or inside a multi-geo list ("LT LV EE" -> three flags). Returns "" when none.
export const affiliateFlags = (label) =>
  (String(label || "").match(/[A-Za-z]{2,}/g) || [])
    .map(countryFlag)
    .filter(Boolean)
    .join("");
// Export для использования в App.jsx countryToContinent (same compound stripping).
export const stripCountryLangSuffix = stripLangSuffix;

// ── Affiliate & Brand lookup tables ──
export const AFFILIATES = [
  { id: "137", name: "Tokomedia" },
  { id: "225", name: "ExTraffic" },
  { id: "211", name: "ExTraffic" },
  { id: "123", name: "MeeseeksMedia" },
  { id: "165", name: "BUFU" },
  { id: "168", name: "PLASH" },
  { id: "83", name: "Leading Media" },
  { id: "194", name: "Q16" },
  { id: "226", name: "Affilihub" },
  { id: "49", name: "Matar" },
  { id: "60", name: "Traffic Lab" },
  { id: "134", name: "Traffic Lab" },
  { id: "183", name: "PandaAds" },
  { id: "159", name: "Farah" },
  { id: "171", name: "MediaPro" },
  { id: "139", name: "20C" },
  { id: "133", name: "Whitefly" },
  { id: "64", name: "Punch" },
  { id: "12", name: "Punch" },
  { id: "51", name: "Liquid Group" },
  { id: "28", name: "Bugle" },
  { id: "131", name: "Bugle" },
  { id: "33", name: "RVG" },
  { id: "167", name: "RVG" },
  { id: "103", name: "Affiliate 103" },
  { id: "101", name: "Affiliate 101" },
  { id: "202", name: "Affiliate 202" },
  { id: "122", name: "Affiliate 122" },
  { id: "222", name: "Affiliate 222" },
  { id: "245", name: "Affiliate 245" },
  { id: "196", name: "Affiliate 196" },
  { id: "71", name: "Affiliate 71" },
  { id: "117", name: "Affiliate 117" },
  { id: "162", name: "Affiliate 162" },
  { id: "164", name: "Affiliate 164" },
  { id: "30", name: "Affiliate 30" },
  { id: "130", name: "Affiliate 130" },
  { id: "140", name: "Affiliate 140" },
  { id: "191", name: "Affiliate 191" },
  { id: "80", name: "Affiliate 80" },
  { id: "17", name: "Affiliate 17" },
  { id: "37", name: "Affiliate 37" },
  { id: "111", name: "Affiliate 111" },
  { id: "212", name: "Affiliate 212" },
];

export const BRANDS_NETWORKS = [
  { id: "152", altId: "162", name: "Helios" },
  { id: "1761", altId: "1941", name: "PRX Ave" },
  { id: "2992", name: "12Mark" },
  { id: "2682", name: "May" },
  { id: "182", name: "Kalipso" },
  { id: "2031", name: "Photonics" },
  { id: "3132", altId: "3122", name: "Fintrix" },
  { id: "582", altId: "592", name: "Avelux / Serenity" },
  { name: "Enthrone" },
  { id: "1071", name: "Evest" },
  { id: "1192", name: "Legion" },
  { id: "2522", name: "UBP" },
  { id: "452", name: "TenX" },
  { name: "Banana" },
  { id: "2642", name: "Crypto Galassia" },
  { id: "1581", name: "DM partners" },
  { name: "Techinvest" },
  { id: "3162", name: "Imperius" },
  { id: "3052", name: "Mi Tang" },
  { name: "TMS" },
  { id: "1971", name: "VenturyFX" },
  { id: "472", name: "Pantheramedia" },
  { id: "3322", name: "MW" },
  { id: "632", name: "StoneWall" },
  { id: "462", name: "GSI markets" },
  { id: "192", name: "Unitraff" },
  { id: "3273", name: "FX-Q" },
  { id: "2722", name: "Theta Holding" },
  { id: "352", altId: "2021", name: "ClickBait" },
  { id: "1871", altId: "302", name: "Capex" },
  { id: "802", name: "UNIT" },
  { id: "642", name: "Miltonia" },
  { id: "2051", name: "No limits" },
  { id: "2171", name: "Michael" },
  { id: "2251", altId: "2261", name: "Fugazi" },
  { id: "1261", name: "Celestia" },
  { id: "282", name: "Level markets" },
  { id: "292", name: "X Brand" },
  { id: "1001", name: "MediaNow" },
  { id: "2952", name: "WhiteRhino" },
  { id: "1601", name: "Fusion" },
  { id: "312", name: "EMP313" },
  { id: "2111", name: "Eurotrade / Z" },
  { id: "252", name: "Monstrack" },
  { id: "3283", name: "BB" },
  { id: "3022", name: "TraderTok" },
  { id: "212", altId: "222", name: "SM / Nexus" },
  { id: "272", altId: "1961", name: "MN" },
  { id: "652", name: "Capitan" },
  { id: "2212", altId: "2232", name: "Swin" },
];

export function getAffiliateName(id) {
  if (!id) return "";
  const found = AFFILIATES.find(a => a.id === String(id));
  return found ? found.name : "";
}

export function getBrandName(id) {
  if (!id) return "";
  const idStr = String(id);
  const found = BRANDS_NETWORKS.find(b => b.id === idStr || b.altId === idStr);
  return found ? found.name : "";
}

// ── Agent name normalization ──
export const AGENT_ALIASES = {
  "john leon": "John", "johnleon": "John", "john l": "John",
  "kate": "Katie", "katey": "Katie",
  "kristy": "Oksana", "kristy b": "Oksana",
  "oleksandra": "Alex", "oleksandra v": "Alex", "oleks": "Alex",
};
export function normalizeAgent(name) {
  if (!name) return name;
  const key = name.trim().toLowerCase();
  return AGENT_ALIASES[key] || name.trim();
}

export function normalizeDealAgent(raw) {
  if (!raw) return '';
  const t = String(raw).trim();
  if (!t) return '';
  return normalizeAgent(t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
}

// ── Person colors ──
export const PEOPLE_COLORS = {
  Alex: "#579BFC", Katie: "#E2445C", Oksana: "#A25DDC", Joy: "#00C875", John: "#38BDF8", Donald: "#FDAB3D",
  // Office / Sophia — явные РАЗНЫЕ цвета (owner request 2026-07-11): без записей обе падали
  // в hash-fallback и получали почти одинаковый зелёный (#4CAF50 / #009688) → неразличимы
  // в колонке Opened By. Office = teal-green, Sophia = indigo.
  Office: "#0EA877", Sophia: "#6366F1",
  // Tat — явный розовый override. Без записи getPersonColor падал в hash-fallback
  // и выдавал #009688 (зелёный). Запрос 2026-06-07: Tat должен быть розовым в
  // CRG Deals (Manage AFF / Made SALE) — и, как следствие общей функции,
  // везде, где используется getPersonColor (Deals, Dashboard, Overview, Partners).
  Tat: "#FF6B9D",
  // John Leon — явный голубой override (запрос 2026-07-12). Полное имя в ячейках
  // Made SALE не совпадает с ключом John → падало в hash-fallback и выдавало
  // #E91E63 (малиновый). Короткий John заодно сменён с #7F5347 (коричневый)
  // на тот же голубой, чтобы один человек был одним цветом везде.
  "John Leon": "#38BDF8",
};
// user_colors фича (2026-07-13, docs/design/_brief_user_colors.md §2.2): резолвинг
// цвета теперь 3-слойный. USER_COLORS_REF (свой цвет юзера, задан через admin UI
// или self-service "My color") проверяется ПЕРВЫМ — побеждает над легаси-картой.
// Если своего цвета нет — старое поведение БЕЗ ИЗМЕНЕНИЙ (PEOPLE_COLORS → hash),
// поэтому все существующие вызовы getPersonColor остаются регрессия-safe.
export const getPersonColor = name => {
  const userColor = USER_COLORS_REF.current.get(name);
  if (userColor) return userColor;
  if (PEOPLE_COLORS[name]) return PEOPLE_COLORS[name];
  let h = 0; for (let i = 0; i < (name||"").length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  const colors = ["#FF6B9D","#00BCD4","#FF9800","#9C27B0","#4CAF50","#E91E63","#3F51B5","#009688"];
  return colors[Math.abs(h) % colors.length];
};

// ── Auto-contrast текста для сплошной person-color заливки (user_colors фича §2.3) ──
// YIQ perceived-brightness формула (r*299+g*587+b*114)/1000, диапазон 0-255.
// Порог 200 (НЕ учебные 128/150!) выбран умышленно как regression-guard: максимальная
// яркость СУЩЕСТВУЮЩЕЙ палитры PEOPLE_COLORS — #FDAB3D (Donald) с YIQ≈183. Учебный
// порог 128-150 переключил бы оранжевый/голубой/розовый на тёмный текст = визуальная
// регрессия таблиц, которые месяцами в проде с БЕЛЫМ текстом поверх этих цветов.
// Полный RGB (custom-цвет юзера) может быть заметно бледнее любого легаси-цвета —
// именно для таких случаев (например пастельно-жёлтый) нужен тёмный текст.
export const textColorFor = (hex) => {
  if (typeof hex !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return '#FFF'; // мусор/undefined → безопасный дефолт, матчит 100% текущих мест использования
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 200 ? '#0F172A' : '#FFF';
};
export const getPersonTextColor = name => textColorFor(getPersonColor(name));

// ── Payment status access control ──
// v31.39: fix — раньше hardcoded whitelist только для 2 emails (y0505300530 = Boss,
// office1092021 = Rose). Alehandro был добавлен в ADMIN_EMAILS 17 апреля (commit 7049c796),
// но этот helper не обновился → Alehandro видел только ["Open", "On the way"] и получал
// "you don't have permission to change it" на status "Paid" в Edit Payment modal.
// Теперь: все admin'ы (из ADMIN_EMAILS) получают полный set. Rose остаётся с restricted
// set без "Approved to pay" (финансовый gate, явный exception перед generic admin check).
export function getAvailableStatuses(userEmail) {
  const FULL = ["Open", "On the way", "On hold", "Approved to pay", "Paid"];
  const BASE = ["Open", "On the way"];
  // Rose — explicit exception: full финансовый workflow кроме "Approved to pay"
  if (userEmail === "office1092021@gmail.com") {
    return ["Open", "On the way", "On hold", "Paid"];
  }
  // Все остальные admin'ы (Boss, Alehandro, + future) — полный set
  if (isAdmin(userEmail)) {
    return FULL;
  }
  return BASE;
}

// ── Fee calculation ──
export function calcFee(fee, amount) {
  if (!fee) return 0;
  // v32.16.73 (2026-06-10): comma-decimal tolerance — production data contains
  // fee "1,5%" (payments.json invoice 254): parseFloat stops at the comma →
  // computed 1% instead of 1.5%. Comma is now treated as a decimal point.
  // Backend twin backend/services/payment-fee.cjs mirrors this exactly
  // (kept in lockstep by a parity test in payment-fee.test.js).
  const f = fee.trim().replace(',', '.');
  const amt = parseFloat(amount) || 0;
  if (f.endsWith('%')) {
    const pct = parseFloat(f.replace('%', ''));
    // H-UT1 (audit 2026-06-04, money): баг — `Math.round(amt * pct / 100)`
    // округлял процентную комиссию до ЦЕЛЫХ долларов (терял центы, мог
    // «придумать» $0.50). flat-ветка ниже центы сохраняет → одна и та же
    // колонка/helper выдавала разную точность, и Invoice-страница
    // (fmtFeeDisplay → `amt.toFixed(2)`) показывала другое число.
    // Фикс: округляем до ЦЕНТОВ, а не до долларов — делим на 100 ПОСЛЕ
    // round, что даёт корректные 2 знака (напр. 33% от 10 = 3.3, а не 3).
    // Backward-compat: единственный потребитель — fmtFee (display-only,
    // через toLocaleString) + Invoice; целочисленной арифметики на
    // результате calcFee нет ни в одном callsite (проверено grep).
    return isNaN(pct) ? 0 : Math.round(amt * pct) / 100;
  }
  const flat = parseFloat(f);
  return isNaN(flat) ? 0 : flat;
}

export function fmtFee(fee, amount) {
  if (!fee) return "\u2014";
  const val = calcFee(fee, amount);
  return fee.trim().endsWith('%') ? `${fee.trim()} (${val.toLocaleString("en-US")}$)` : `${val.toLocaleString("en-US")}$`;
}

// totalWithFee(p) — DEBT TOTAL платежа: base amount + calcFee, округл. до центов.
// Фронт-твин backend/services/payment-fee.cjs totalWithFee (держать в lockstep).
export function totalWithFee(p) {
  const base = Number(p && p.amount || 0) || 0;
  return Math.round((base + calcFee(p && p.fee, p && p.amount)) * 100) / 100;
}

// ── Page access control ──
// ALL_PAGES loaded from shared/pages.json (imported at top of file).
// Backend server.cjs читает ту же JSON через fs.readFileSync — rename page key
// в shared/pages.json автоматически синхронизируется с backend migration.
export const ALL_PAGES = sharedPages;

// v32.13: pages every user can reach regardless of their stored pageAccess.
// 'ai' (AI Advisor) is "visible to all" per spec — appended below for both admins
// and regular users so canAccess('ai') is true without editing each user's
// pageAccess in users.json or adding 'ai' to shared/pages.json. Additive only:
// existing per-page access is untouched; we only ensure these keys are present.
export const PUBLIC_PAGES = ['ai', 'radar'];
const withPublicPages = (keys) => [...keys, ...PUBLIC_PAGES.filter(p => !keys.includes(p))];

export function getPageAccess(user) {
  if (isAdmin(user.email)) return withPublicPages(ALL_PAGES.map(p => p.key));
  if (!user.pageAccess || !Array.isArray(user.pageAccess) || user.pageAccess.length === 0) {
    return withPublicPages(["overview", "settings"]);
  }
  return withPublicPages(user.pageAccess);
}

// ── SHA-256 password hashing (pure JS) ──
export function hashPassword(password) {
  // Pure JS SHA-256 implementation
  function sha256(ascii) {
    const mathPow = Math.pow;
    const maxWord = mathPow(2, 32);
    let result = '';
    const words = [];
    const asciiBitLength = ascii.length * 8;
    let hash = [];
    const k = [];
    let primeCounter = 0;

    const isComposite = {};
    for (let candidate = 2; primeCounter < 64; candidate++) {
      if (!isComposite[candidate]) {
        for (let i = 0; i < 313; i += candidate) isComposite[i] = candidate;
        hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
        k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      }
    }

    ascii += '\x80';
    while ((ascii.length % 64) - 56) ascii += '\x00';
    for (let i = 0; i < ascii.length; i++) {
      const j = ascii.charCodeAt(i);
      if (j >> 8) return;
      words[i >> 2] |= j << (((3 - i) % 4) * 8);
    }
    words[words.length] = (asciiBitLength / maxWord) | 0;
    words[words.length] = asciiBitLength;

    for (let j = 0; j < words.length;) {
      const w = words.slice(j, (j += 16));
      const oldHash = hash.slice(0);
      hash = hash.slice(0, 8);
      for (let i = 0; i < 64; i++) {
        const w15 = w[i - 15], w2 = w[i - 2];
        const a = hash[0], e = hash[4];
        const temp1 = hash[7] +
          (((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))) +
          ((e & hash[5]) ^ (~e & hash[6])) +
          k[i] +
          (w[i] = i < 16 ? w[i] : (
            w[i - 16] +
            (((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3)) +
            w[i - 7] +
            (((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10))
          ) | 0);
        const temp2 = (((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))) +
          ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
        hash = [(temp1 + temp2) | 0].concat(hash);
        hash[4] = (hash[4] + temp1) | 0;
      }
      for (let i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
    }

    for (let i = 0; i < 8; i++) {
      for (let j = 3; j + 1; j--) {
        const b = (hash[i] >> (j * 8)) & 255;
        result += (b < 16 ? '0' : '') + b.toString(16);
      }
    }
    return result;
  }
  return sha256(password);
}

// ── Search matcher (digit-aware) ──────────────────────────────────────────────
// 2026-05-15 PR#47: extracted после PR#46 sub-agent finding PSC-1 — same
// digit-substring filter pattern lived в FtdsInfo + CRGDeals + Deals + AutoB2026
// + Invoice. Pre-fix: `.includes("47")` matched "47", "470", "472", "4711"
// (any field containing "47") — false positives on numeric ID/amount searches.
//
// Fix: detect pure-digit query (`/^\d+$/`) → use word-boundary regex (`\b47\b`)
// per field. Non-digit text searches keep `.includes()` substring behavior для
// partial-name matching ("acm" → "Acme").
//
// Edge cases handled:
//   - searchTerm leading/trailing whitespace → trimmed FIRST (PR#46 sub-agent #3
//     finding: trailing space defeats fix via substring fallback)
//   - empty/null/undefined input → returns null (caller should skip filter)
//   - non-string field values (numbers, null) → coerced via String()
//
// Word-boundary `\b` semantics:
//   ✅ "47 Aff Smith" matches query "47" (boundary at space)
//   ✅ "*47" matches "47" (* is non-\w → boundary)
//   ✅ "Aff-47-name" matches "47" (hyphens delimit)
//   ✅ "$47.00" matches "47" (boundary at $ and .)
//   ✅ "470", "472", "4700" do NOT match "47" (digit-digit adjacency = no boundary)
//   ⚠️ "Aff_47_name" does NOT match "47" (`_` is `\w` char) — rare в production data
//   ⚠️ "47abc" does NOT match "47" (digit-letter adjacency) — rare
//
// @param {string} searchTerm raw user input
// @returns {((field: string|number|null|undefined) => boolean) | null} matcher
//          per field, OR null if search query is empty (caller should skip filter)
export function digitAwareSearchMatcher(searchTerm) {
  const s = String(searchTerm || '').trim().toLowerCase();
  if (!s) return null;
  const sIsDigits = /^\d+$/.test(s);
  const wbRe = sIsDigits ? new RegExp('\\b' + s + '\\b') : null;
  return (field) => {
    const f = String(field || '').toLowerCase();
    return sIsDigits ? wbRe.test(f) : f.includes(s);
  };
}

// ───────────────────────────────────────────────────────────────────────────
// splitPricePayout — extract combined price string "1500+14%" → { base, sign, payout }
// ───────────────────────────────────────────────────────────────────────────
//
// Used by Offers display layer (frontend OffersTree + edit dialog + DM bot reply)
// to avoid duplicate "%" display. Single source of truth (PR-C sub-agent HIGH #3 fix).
//
// Accepts: "1500+14%", "1500-3%", "1500+14", "1500.5+12.3%", "$1500+14%", "1,500+14%".
// Strips leading `$` and comma thousands separators before matching.
//
// 2026-05-22 PR#147 range-payout extension: payout group now accepts ranges.
//   "1500+8-10%" → { base: '1500', sign: '+', payout: '8-10' }
//   "1500+8.5-10.5%" → { base: '1500', sign: '+', payout: '8.5-10.5' }
// MUST stay в sync с 2 mirrored copies:
//   • backend/services/deals-dedup.cjs::splitPricePayout
//   • backend/services/offers-query.cjs::splitPricePayout
//
// Returns: { base: '1500', sign: '+', payout: '14' | '8-10' | '8.5-10.5' } OR null.
//
// HIGH #1 fix: sign preserved (was always converted к '+'). HIGH #2 fix: numeric
// validation prevents pathological inputs like '---' or '..-.' from passing.
// MED #6 fix: tolerates `$` prefix + comma thousands separators.
export function splitPricePayout(priceStr) {
  if (priceStr == null) return null;
  const cleaned = String(priceStr).trim().replace(/^\$/, '').replace(/,/g, '');
  const m = cleaned.match(/^(\d+(?:\.\d+)?)\s*([+\-])\s*(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?)\s*%?\s*$/);
  if (!m) return null;
  return { base: m[1], sign: m[2], payout: m[3] };
}

// formatCrgDisplay — normalize crg field value к display string `+N%` или `-N%`.
// Handles bare numbers ("3" → "+3%"), pre-formatted ("+15%" → "+15%"), or literal
// garbage from old parser ("CRG" → '' suppressed). Used by DM bot reply rendering.
//
// 2026-05-22 PR#147 range-payout extension: accept range form ("8-10" → "+8-10%",
// "+8-10" → "+8-10%"). MUST stay в sync с offers-query.cjs::formatCrgDisplay.
//
// HIGH #2 fix: rejects negative bare values that would render как invalid "+-3%".
export function formatCrgDisplay(crgRaw) {
  const s = String(crgRaw || '').trim();
  if (!s) return '';
  if (/^CRG$/i.test(s)) return ''; // legacy garbage from PASS 4 fix predecessor
  // Already formatted (starts with sign)?
  if (s.startsWith('+') || s.startsWith('-')) {
    // Validate body — accept single OR range
    return /^[+\-]\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*%?$/.test(s) ? (s.includes('%') ? s : s + '%') : '';
  }
  // Bare number/range → prepend +
  if (/^\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?$/.test(s)) return `+${s}%`;
  return ''; // unrecognized format
}

// ── Number input scroll guard ──
// FIX 1: onWheel handler for number inputs — prevents accidental value change on
// scroll. Moved verbatim from App.jsx (C1 refactor). Spread onto <input type="number">.
export const noScrollNum = { onWheel: e => e.target.blur() };
